import {LogLevel} from './log-level';
import {LoggerConfig} from './logger-config';
import {ConsoleAppender} from './appenders/console/console-appender';
import {LocalStorageAppender} from './appenders/local-storage/local-storage-appender';
import {XhrAppender} from './appenders/xhr/xhr-appender';
import {ConfigService} from '../config/config-service';
import {LogMessage} from './log-message';
import {stringify} from '../utils/stringify';
import {inject, Injectable} from '../di';
import {SentryBreadcrumbsAppender} from './appenders/sentry-breadcrumbs/sentry-breadcrumbs-appender';
import {filter, map} from 'rxjs';

const DEFAULT_TRIGGER_LEVEL: LogLevel = LogLevel.Debug;
const DEFAULT_CONTEXT_SIZE: number = 50;

@Injectable()
export class Logger {
  private readonly configService = inject(ConfigService);

  private readonly appenders = [
    inject(SentryBreadcrumbsAppender),
    inject(ConsoleAppender),
    inject(LocalStorageAppender),
    inject(XhrAppender),
  ];

  /**
   * Log messages in-memory cache
   */
  protected history: LogMessage[] = [];

  private config!: LoggerConfig;

  constructor() {
    this.configService.config$
      .pipe(
        map((c) => c?.logger),
        filter((c) => !!c),
      )
      .subscribe((c) => this.applyConfig(c));
  }

  private applyConfig(config?: LoggerConfig) {
    if (this.config && this.config === config) {
      return;
    }

    // validation and fixing config:
    if (!config) {
      // Default config
      config = new LoggerConfig();
    }
    if (config.triggerLevel == null) {
      config.triggerLevel = DEFAULT_TRIGGER_LEVEL;
    }
    if (config.contextSize == null || config.contextSize <= 0) {
      config.contextSize = DEFAULT_CONTEXT_SIZE;
    }

    // applying config
    this.config = config;
  }

  private processLogMessage(level: LogLevel, args: any[]) {
    if (!args || !args.length || level < this.config.triggerLevel) {
      return;
    }

    // Add log history entry
    const logMessage = this.createLogMessage(level, args);

    for (const appender of this.appenders) {
      try {
        appender.log(logMessage, level, args, this.history);
      } catch (err) {
        console.error(`[Logger] Unexpected logger appender error`, err);
      }
    }

    this.history.push(logMessage);

    // Limit in-memory cache size
    if (this.history.length > this.config.contextSize) {
      this.history.shift();
    }
  }

  private createLogMessage(level: LogLevel, args: any[]): LogMessage {
    const msgStr = args.map((e) => stringify(e)).join('\n\n');
    return {
      message: msgStr,
      level: level,
      timestamp: new Date(),
    };
  }

  /**
   * Publish critical log message.
   *
   * Use this level for situations where the entire application is not functioning properly.
   * Such situations should be investigated.
   */
  public critical(...args: any[]) {
    this.processLogMessage(LogLevel.Critical, args);
  }

  /**
   * Publish error log message.
   *
   * Use this level for situations where an unexpected or unrecoverable error has occurred, which, however, did not
   * lead to the collapse of the entire system, but affected only some non-critical subsystems. Such things include the
   * unavailability of some resources, broadcast streams, local module errors, etc.
   *
   */
  public error(...args: any[]) {
    this.processLogMessage(LogLevel.Error, args);
  }

  /**
   * Publish warning log message.
   *
   * Use this level for situations where a predictable error or expected situation has occurred, which, however, should
   * not have occurred during normal operation of the system. Such errors do not affect the functionality of any
   * systems, but, nevertheless, they are not part of the expected behavior of the system.
   */
  public warn(...args: any[]) {
    this.processLogMessage(LogLevel.Warn, args);
  }

  /**
   * Publish info log message.
   *
   * Use this level for normal informational messages. For example, messages about the application startup, its
   * version, environment data, as well as some significant situations that are not errors. Usually, this level is
   * displayed in the console in the Development mode of the application, unlike the Debug level.
   */
  public log(...args: any[]) {
    this.processLogMessage(LogLevel.Info, args);
  }

  /**
   * Publish debug log message.
   *
   * Use this level to mark the progress of the application at every more or less significant stage. For example,
   * opening/closing a page, user actions, button clicks, commands from the server, start/end of data loading, etc.
   * This data will be used for debugging as a context when more serious errors occur. Typically, this level is hidden
   * in the Development mode of the application.
   */
  public debug(...args: any[]) {
    this.processLogMessage(LogLevel.Debug, args);
  }
}
